import { computed, ref } from 'vue'
import { useAuth } from './useAuth'

/**
 * DEV-239: roles the backend accepts for overriding the identical-mileage
 * BLOCK (DuplicateTurnaroundDetector::OVERRIDE_ROLES). The backend allowed
 * this since DEV-220, but the PWA never offered it — the back office was
 * stuck behind "Bitte die Disposition kontaktieren" while BEING the
 * Disposition. Keep in sync with the backend constant.
 */
const BLOCK_OVERRIDE_ROLES = ['dispatcher', 'disponent', 'schichtleiter', 'admin']

/**
 * DEV-220 — client side of the duplicate-turnaround guard.
 *
 * The backend runs the same guard on two endpoints:
 *   - POST /api/checker/checkins                       (create with services)
 *   - POST /api/vehicle-checkins/{id}/add-services     (the live PWA path)
 * and answers identically:
 *   - 409 { error: 'duplicate_turnaround', requires_reason, message, details }
 *     → ask the user; retry the SAME request with confirm_duplicate_turnaround
 *       (+ duplicate_turnaround_reason, min. 10 chars, when requires_reason)
 *   - 422 with the validation key `duplicate_turnaround` → hard block
 *     (identical mileage), no retry is offered.
 *
 * POST /api/vehicle-checkin-services (per-service attach of the initial
 * selection) sends the confirmable case as 422 + error: 'duplicate_turnaround'
 * instead of 409 — pass `isConflict: isDuplicateTurnaroundAttachConflict`.
 *
 * This composable owns that state machine; the markup lives in
 * components/DuplicateTurnaroundModal.vue.
 */

export interface DuplicateTurnaroundDetails {
  level?: string
  previous_checkin_id?: number
  previous_checkin_at?: string
  previous_mileage?: number | null
  mileage?: number | null
  delta_km?: number | null
  hours?: number
  message?: string
}

export interface DuplicateTurnaroundResponse {
  requires_reason?: boolean
  message?: string
  details?: DuplicateTurnaroundDetails
}

type Payload = Record<string, any>

export interface UseDuplicateTurnaroundOptions<TPayload extends Payload> {
  /**
   * Issues the guarded request and performs the whole success handling
   * (navigation etc.), so the confirmation retry behaves exactly like the
   * first attempt. Must reject on failure.
   */
  submit: (payload: TPayload) => Promise<void>
  /** Called with the server message for the non-overridable 422 hard block. */
  onBlocked: (message: string) => void
  /** Fallback text shown inside the modal when the confirmation retry fails. */
  confirmErrorMessage?: string
  /**
   * How a confirmable duplicate-turnaround answer is recognised. Defaults to the
   * 409 shape; the per-service attach endpoint uses the 422 shape instead
   * (`isDuplicateTurnaroundAttachConflict`).
   */
  isConflict?: (err: any) => boolean
}

/**
 * 409 that carries error === 'duplicate_turnaround'. Other 409s (e.g. "Fahrzeug
 * hat bereits einen aktiven Check-In") must keep their old behaviour, so never
 * match on the status code alone.
 */
export const isDuplicateTurnaroundConflict = (err: any): boolean =>
  err?.response?.status === 409 && err?.response?.data?.error === 'duplicate_turnaround'

/**
 * 422 that carries error === 'duplicate_turnaround'. POST /api/vehicle-checkin-services
 * (the per-service attach of the initial selection) answers with 422 instead of 409,
 * because that loop historically skips every 409 silently.
 */
export const isDuplicateTurnaroundAttachConflict = (err: any): boolean =>
  err?.response?.status === 422 && err?.response?.data?.error === 'duplicate_turnaround'

/**
 * 422 carrying the duplicate_turnaround validation key (and NO `error` field):
 * hard block, identical mileage, not overridable.
 */
export const duplicateTurnaroundValidationMessage = (err: any): string | null => {
  if (err?.response?.status !== 422) return null
  const errors = err.response?.data?.errors ?? {}
  const entry = errors.duplicate_turnaround ?? errors.duplicate_turnaround_reason
  if (!entry) return null
  return Array.isArray(entry) ? entry[0] : String(entry)
}

export function useDuplicateTurnaround<TPayload extends Payload>(
  options: UseDuplicateTurnaroundOptions<TPayload>
) {
  const showModal = ref(false)
  const data = ref<DuplicateTurnaroundResponse | null>(null)
  const reason = ref('')
  const modalError = ref('')
  const isConfirming = ref(false)
  /** DEV-239: the modal is overriding an identical-mileage block. */
  const isBlockOverride = ref(false)
  const { activeRole } = useAuth()
  // Payload of the request that ran into the guard, so the confirmation can
  // re-issue exactly the same call plus the confirm flag — and ONLY that call.
  const pendingPayload = ref<TPayload | null>(null)

  const matchesConflict = options.isConflict ?? isDuplicateTurnaroundConflict

  const needsReason = computed(() => data.value?.requires_reason === true)

  const canConfirm = computed(() => {
    if (isConfirming.value) return false
    if (!needsReason.value) return true
    return reason.value.trim().length >= 10
  })

  const close = () => {
    showModal.value = false
    data.value = null
    reason.value = ''
    modalError.value = ''
    pendingPayload.value = null
    isBlockOverride.value = false
  }

  /**
   * Runs `submit(payload)`.
   * @returns true when the request succeeded, false when a duplicate-turnaround
   *          case was handled (modal opened, or hard block reported via
   *          onBlocked). Every other error is rethrown to the caller.
   */
  const submitGuarded = async (payload: TPayload): Promise<boolean> => {
    pendingPayload.value = payload
    try {
      await options.submit(payload)
      pendingPayload.value = null
      return true
    } catch (err: any) {
      if (matchesConflict(err)) {
        data.value = err.response.data
        reason.value = ''
        modalError.value = ''
        showModal.value = true
        return false
      }

      const blockMessage = duplicateTurnaroundValidationMessage(err)
      if (blockMessage) {
        // DEV-239: the backend accepts an override of the identical-mileage
        // block from back-office roles (confirmed + reason). Offer it to
        // exactly those roles; everyone else keeps the hard block.
        if (BLOCK_OVERRIDE_ROLES.includes(activeRole.value ?? '')) {
          data.value = {
            requires_reason: true,
            message: blockMessage
              + ' Als Disposition/Schichtleitung können Sie mit Begründung trotzdem buchen.'
          }
          reason.value = ''
          modalError.value = ''
          isBlockOverride.value = true
          showModal.value = true
          return false
        }

        // Hard block (identische Kilometer) — kein Retry anbieten.
        pendingPayload.value = null
        options.onBlocked(blockMessage)
        return false
      }

      pendingPayload.value = null
      throw err
    }
  }

  /**
   * Re-send the identical request with the confirmation flag (+ reason).
   * A second failure is only displayed — never retried automatically.
   */
  const confirm = async () => {
    const payload = pendingPayload.value
    if (!payload || !canConfirm.value) return

    const trimmed = reason.value.trim()
    isConfirming.value = true
    modalError.value = ''

    try {
      await options.submit({
        ...payload,
        confirm_duplicate_turnaround: true,
        ...(trimmed ? { duplicate_turnaround_reason: trimmed } : {})
      })
      showModal.value = false
      data.value = null
      reason.value = ''
      pendingPayload.value = null
      isBlockOverride.value = false
    } catch (err: any) {
      console.error('Fehler beim Bestätigen des Turnarounds:', err)

      if (matchesConflict(err)) {
        // Backend still not satisfied (e.g. reason now required) — update the
        // modal instead of looping.
        data.value = err.response.data
        modalError.value = err.response.data?.message
          || 'Der Turnaround konnte nicht bestätigt werden.'
      } else {
        modalError.value = duplicateTurnaroundValidationMessage(err)
          || err?.response?.data?.message
          || options.confirmErrorMessage
          || 'Speichern fehlgeschlagen. Bitte die Disposition kontaktieren.'
      }
    } finally {
      isConfirming.value = false
    }
  }

  return {
    showModal,
    data,
    reason,
    modalError,
    isConfirming,
    isBlockOverride,
    needsReason,
    canConfirm,
    submitGuarded,
    confirm,
    close
  }
}
